import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Spin, Tooltip as AntTooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Cell,
    Legend,
    Pie,
    PieChart as RPieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import { getNamespaceGapsApi } from '../../../api/customerApi';
import { DECODED_ACCESS_TOKEN } from '../../../constants/system.keys';
import storageService from '../../../services/storage.service';
import { getTotalRequiredSecondsPerDevice } from '../../../untils/date';

const formatDuration = (seconds, t) => {
    if (!seconds || seconds <= 0) return `0 ${t ? t('hours') : 'hours'}`;
    const hours = (seconds / 3600).toFixed(2);
    return `${Number(hours).toLocaleString()} ${t ? t('hours') : 'hours'}`;
};

const CustomStorageTooltip = ({ active, payload, t }) => {
    if (active && payload && payload.length) {
        const dataItem = payload[0].payload;
        const total = dataItem.total || 1;
        const percent = ((dataItem.value / total) * 100).toFixed(2);
        return (
            <div
                style={{
                    backgroundColor: '#000',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #333',
                }}
            >
                <div style={{ color: dataItem.color, fontWeight: 'bold', marginBottom: '4px' }}>
                    {`${dataItem.name}: ${percent}%`}
                </div>
                <div style={{ color: '#ccc', fontSize: '12px' }}>
                    {`${t('Duration')}: ${formatDuration(dataItem.value, t)}`}
                </div>
            </div>
        );
    }
    return null;
};

const renderLegend = (props, t) => {
    const { payload } = props;
    return (
        <ul className="legend-wrapper" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
            {payload?.map((entry, index) => {
                const itemData = entry.payload || {};
                const name = itemData.name || entry.value || '';
                const count = itemData.value || 0;
                const total = itemData.total || 1;
                const percent = ((count / total) * 100).toFixed(2);
                return (
                    <li
                        className="item-legend"
                        style={{
                            color: entry.color,
                            marginBottom: '6px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        key={`item-${index}`}
                    >
                        <span
                            className="bullet"
                            style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                background: entry.color,
                                marginRight: '8px',
                                borderRadius: '2px',
                            }}
                        ></span>
                        {`${name} - ${percent}% (${formatDuration(count, t)})`}
                    </li>
                );
            })}
        </ul>
    );
};

const OnlineCameraSLA = ({ dateRanger }) => {
    const { t } = useTranslation();
    const { namespace, cameras } = useSelector((state) => state);
    const [loading, setLoading] = useState(false);

    const fromDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[0]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[0]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const toDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[1]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[1]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const totalDays = useMemo(() => {
        if (!dateRanger || !dateRanger[0] || !dateRanger[1]) return 30;
        const start = moment(dateRanger[0]).startOf('day');
        const end = moment(dateRanger[1]).endOf('day');
        const diff = end.diff(start, 'days') + 1;
        return diff > 0 ? diff : 1;
    }, [dateRanger]);

    const [gapInfo, setGapInfo] = useState(null);

    const fetchGaps = useCallback(async () => {
        if (!namespace) return;
        setLoading(true);
        try {
            const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
            const dataQuery = {
                fromDate: fromDateStr,
                toDate: toDateStr,
            };
            const res = await getNamespaceGapsApi(token, { namespace }, dataQuery);
            if (res && res.data) {
                const apiData = res.data.data || res.data;
                setGapInfo(apiData);
            } else {
                setGapInfo(null);
            }
        } catch (err) {
            console.error('Error fetching namespace gaps:', err);
            setGapInfo(null);
        } finally {
            setLoading(false);
        }
    }, [namespace, fromDateStr, toDateStr]);

    useEffect(() => {
        fetchGaps();
    }, [fetchGaps]);

    const summary = gapInfo?.summary || gapInfo;
    const numberOnline = summary?.cameraOnline ?? cameras?.numberOnline ?? 0;

    const storageStats = useMemo(() => {
        const totalSecondsPerCamera = getTotalRequiredSecondsPerDevice(dateRanger);
        // Total cần lưu trữ = số camera online * số giây yêu cầu
        const totalRequiredSeconds = numberOnline * totalSecondsPerCamera;

        let totalGapSeconds = 0;
        let uploadableGapSeconds = 0;

        if (summary) {
            if (typeof summary.totalGapS === 'number') {
                totalGapSeconds = summary.totalGapS;
                uploadableGapSeconds = summary.totalUploadS || 0;
            } else if (Array.isArray(summary)) {
                summary.forEach((gap) => {
                    totalGapSeconds += gap.durationS || gap.duration || 0;
                    uploadableGapSeconds += gap.uploadS || gap.uploadableSeconds || 0;
                });
            }
        }

        const unuploadableGapSeconds = Math.max(0, totalGapSeconds - uploadableGapSeconds);
        const totalUploadedSeconds = Math.max(0, totalRequiredSeconds - totalGapSeconds);

        const safeTotal = totalRequiredSeconds > 0 ? totalRequiredSeconds : 1;
        const uploadPercent = ((totalUploadedSeconds / safeTotal) * 100).toFixed(2);

        return {
            totalRequiredSeconds,
            totalUploadedSeconds,
            totalGapSeconds,
            uploadableGapSeconds,
            unuploadableGapSeconds,
            uploadPercent,
        };
    }, [numberOnline, dateRanger, summary]);

    const pieChartData = useMemo(() => {
        const {
            totalRequiredSeconds,
            totalUploadedSeconds,
            uploadableGapSeconds,
            unuploadableGapSeconds,
        } = storageStats;

        const total = totalRequiredSeconds || 1;

        const data = [
            {
                name: t('Stored'),
                value: totalUploadedSeconds,
                color: '#0E9347', // Xanh lá
                total,
            },
            {
                name: t('Missing - Recoverable'),
                value: uploadableGapSeconds,
                color: '#f59e0b', // Vàng / Cam
                total,
            },
        ];

        if (unuploadableGapSeconds > 0) {
            data.push({
                name: t('Missing - Unrecoverable'),
                value: unuploadableGapSeconds,
                color: '#ef4444', // Đỏ
                total,
            });
        }

        return data;
    }, [storageStats, t]);

    if (namespace !== "hdbank.com.vn") return null;

    return (
        <div className="chart-item content-center" style={{ width: '50%', position: 'relative' }}>
            <AntTooltip title={t('Reload')}>
                <ReloadOutlined
                    onClick={fetchGaps}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        color: '#8c8c8c',
                        fontSize: '16px',
                        cursor: 'pointer',
                        zIndex: 10,
                    }}
                />
            </AntTooltip>
            <div
                className="chart-title"
                style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#fff',
                    marginBottom: '16px',
                }}
            >
                {t('Online Camera Storage Rate')}
            </div>
            {loading ? (
                <div
                    style={{
                        height: '260px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Spin size="default" />
                </div>
            ) : (
                <Row style={{ width: '100%', alignItems: 'center' }}>
                    <Col span={6}>
                        <div className="extra-info" style={{ textAlign: 'center' }}>
                            <div style={{ color: '#aaa', fontSize: '14px', marginBottom: '8px' }}>
                                {t('Storage Rate')}
                            </div>
                            <div
                                className="total-device"
                                style={{ fontSize: '32px', fontWeight: 'bold', color: '#0E9347' }}
                            >
                                {`${storageStats.uploadPercent}%`}
                            </div>
                        </div>
                    </Col>
                    <Col span={18}>
                        <ResponsiveContainer width="100%" height={260}>
                            <RPieChart width={500} height={260}>
                                <Pie
                                    dataKey="value"
                                    isAnimationActive={false}
                                    data={pieChartData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    innerRadius={45}
                                    stroke="none"
                                >
                                    {pieChartData?.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomStorageTooltip t={t} />} />
                                <Legend
                                    layout="vertical"
                                    align="left"
                                    verticalAlign="middle"
                                    content={(props) => renderLegend(props, t)}
                                />
                            </RPieChart>
                        </ResponsiveContainer>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default OnlineCameraSLA;
